import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { UserService } from 'app/store/services/user.service';
import { SessionService } from 'app/store/services/session.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IUserSessionData } from 'app/store/models/user.model';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
})
export class ChangePasswordComponent implements OnInit, OnDestroy {
  sessionData: IUserSessionData;

  formGroup: FormGroup;

  cancelButtonProperties: Button = {
    text: 'Cancel',
    color: ButtonColor.RED,
    size: ButtonSize.SMALL,
    route: ['/accounts', 'settings'],
    style: 'rounded',
  };

  updateButtonProperties: Button = {
    text: 'Save',
    color: ButtonColor.ORANGE,
    size: ButtonSize.SMALL,
    style: 'rounded',
    onClick: () => this.updatePassword(),
  };

  error: { old?: string; new?: string; confirm?: string };

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private userService: UserService,
    private sessionService: SessionService,
    private router: Router,
    private matSnackBar: MatSnackBar
  ) {}

  get oldPassword() {
    return this.formGroup.get('current_password');
  }

  get newPassword() {
    return this.formGroup.get('new_password');
  }

  get confirmPassword() {
    return this.formGroup.get('confirm_password');
  }

  addError(key: 'old' | 'new' | 'confirm', message: string) {
    if (!this.error) {
      this.error = {};
    }

    this.error[key] = message;

    Object.keys(this.error).forEach(k => {
      if (k !== key) {
        this.error[k] = null;
      }
    });
  }

  updatePassword() {
    if (this.formGroup.valid) {
      this.updateButtonProperties.observeApi = true;
      this.userService
        .changePassword(
          this.sessionData.id,
          this.oldPassword.value,
          this.newPassword.value,
          this.confirmPassword.value
        )
        .subscribe(
          () => {
            this.updateButtonProperties.observeApi = false;
            this.matSnackBar.open('Password changed successfully', null, {
              duration: 1000,
              verticalPosition: 'top',
              panelClass: ['alert-success'],
            });
            this.router.navigate(['/']);
          },
          error => this.formatError(error)
        );
    } else {
      this.formGroup.markAllAsTouched();
    }
  }

  formatError(error: HttpErrorResponse) {
    this.updateButtonProperties.observeApi = false;
    if (error.error) {
      let key = null;
      switch (error.error.code) {
        case 1003:
          key = 'old';
          break;
        case 1004:
          key = 'new';
          break;
        default:
          key = 'confirm';
          break;
      }

      this.addError(key, error.error.message);
    }
  }

  ngOnInit() {
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(sessionData => {
        this.formGroup = new FormGroup({
          current_password: new FormControl('', [Validators.required]),
          new_password: new FormControl('', [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern('^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*\\W).{8,}$'),
          ]),
          confirm_password: new FormControl('', [
            Validators.required,
            Validators.minLength(8),
            (control: FormControl) => {
              if(!this.formGroup) {
                return null;
              }
              if (control.value !== this.formGroup.get('new_password').value) {
                return { passwordsDontMatch: true };
              }
              return null;
            },
          ]),
        });
        this.sessionData = sessionData;

      });
  }

  ngOnDestroy() {
    this.destroy$.next(false);
  }

  get confirm_password(): AbstractControl {
    return this.formGroup.get('confirm_password');
  }

  hasControlErrors(control: AbstractControl) {
    return control.invalid && (control.dirty || control.touched);
  }
}
